import { useEffect, useRef } from 'react';

import { TaskStatus, TaskView } from '@ndvr/customer-api';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { titleCase } from 'utils';

import { useTaskViewState } from '../task-state';

interface TaskStatusFilterProps {
    tasks: TaskView[];
    searchTerm: string;
    onSearchChange: (value: string) => void;
    focusSearchTrigger: number;
}

export const TaskStatusFilter = ({ tasks, searchTerm, onSearchChange, focusSearchTrigger }: TaskStatusFilterProps) => {
    const { taskViewState, setTaskViewState } = useTaskViewState();
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Get unique task statuses from the provided task data
    const availableTaskStatuses = (() => {
        if (!tasks.length) return [];

        const uniqueTaskStatuses = Array.from(
            new Set(tasks.map((task) => task.status).filter((status) => status != null)),
        );

        return uniqueTaskStatuses
            .map((status) => ({
                status,
                label: titleCase(status),
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    })();

    // Filter task statuses based on search term
    const filteredTaskStatuses = availableTaskStatuses.filter((taskStatus) =>
        taskStatus.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const handleTaskStatusToggle = (taskStatus: TaskStatus) => {
        const currentSelected = taskViewState.selectedTaskStatuses;
        const isSelected = currentSelected.includes(taskStatus);

        const newSelected = isSelected
            ? currentSelected.filter((status) => status !== taskStatus)
            : [...currentSelected, taskStatus];

        setTaskViewState({ selectedTaskStatuses: newSelected });
    };

    // Focus search input when requested
    useEffect(() => {
        if (focusSearchTrigger > 0) {
            searchInputRef.current?.focus();
        }
    }, [focusSearchTrigger]);

    return (
        <div className="flex h-full flex-col space-y-3">
            <div className="p-4 pt-2">
                <Input
                    ref={searchInputRef}
                    placeholder="Search statuses..."
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="w-full"
                />
            </div>
            <div className="flex-1 overflow-y-auto">
                <div className="space-y-2">
                    {filteredTaskStatuses.length === 0 ? (
                        <div className="py-4 text-center text-sm text-gray-500">No statuses found</div>
                    ) : (
                        filteredTaskStatuses.map((taskStatus) => {
                            const isSelected = taskViewState.selectedTaskStatuses.includes(taskStatus.status);
                            return (
                                <div
                                    key={taskStatus.status}
                                    className="flex cursor-pointer items-center space-x-3 rounded p-2 hover:bg-gray-50"
                                    onClick={() => handleTaskStatusToggle(taskStatus.status)}
                                >
                                    <Checkbox checked={isSelected} />
                                    <div className="flex items-center">
                                        <span>{taskStatus.label}</span>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};
